import {
  Box,
  Flex,
  SimpleGrid,
  Stack,
  Text,
  Tag,
  TagLabel,
  HStack,
  Wrap,
  WrapItem,
  Link,
  Image,
  Divider
} from '@chakra-ui/react'

import Regions from '../../resources/Regions'
import {
  SERVICE_TYPES,
  SERVICE_FREQUENCIES
} from '../../resources/ContractedServices'
import addFileIcon from '../../assets/images/addFile.svg'

// --- Helpers de etiquetas ---
const getRegionName = (number) => {
  const region = Regions.find((r) => r.number === String(number))
  return region ? region.name : number || '—'
}

const getCommuneName = (identifier) => {
  for (const region of Regions) {
    const commune = region.communes.find((c) => c.identifier === identifier)
    if (commune) return commune.name
  }
  return identifier || '—'
}

const getServiceTypeLabel = (value) =>
  SERVICE_TYPES.find((s) => s.value === value)?.label || value

const getFrequencyLabel = (value) =>
  SERVICE_FREQUENCIES.find((f) => f.value === value)?.label || value

// --- Primitivos UI ---
const SectionTitle = ({ children }) => (
  <Text fontSize="18px" fontWeight="bold" color="#36355F" mb={3}>
    {children}
  </Text>
)

const Field = ({ label, value }) => (
  <Box>
    <Text fontSize="12px" color="#718096" textTransform="uppercase" letterSpacing="0.5px">
      {label}
    </Text>
    <Text fontSize="15px" color="#36355F" fontWeight="500" wordBreak="break-word">
      {value || '—'}
    </Text>
  </Box>
)

const Card = ({ children }) => (
  <Box
    bg="#FFFFFF"
    border="1px solid"
    borderColor="#E2E8F0"
    borderRadius="xl"
    boxShadow="sm"
    p={6}
  >
    {children}
  </Box>
)

const PestStat = ({ label, value }) => (
  <Box bg="#F7FBFC" border="1px solid" borderColor="#D1F1F5" borderRadius="lg" p={4} textAlign="center">
    <Text fontSize="28px" fontWeight="bold" color="#59BABA" lineHeight="1.1">
      {value ?? 0}
    </Text>
    <Text fontSize="12px" color="#36355F" mt={1}>
      {label}
    </Text>
  </Box>
)

// Perfil read-only del cliente (visible para todos los roles)
const ClientProfile = ({ client }) => {
  if (!client) return null

  const branchs = client.branchs || []
  const locations = client.locations || []
  const services = client.contracted_services || []
  const pest = client.pest_control_stations || {}
  const isCompany = client.type === 'COMPANY'

  return (
    <Stack spacing={6}>
      {/* Tipo de cliente */}
      <Card>
        <SectionTitle>Tipo de cliente</SectionTitle>
        <Tag size="lg" bg="#D1F1F5" color="#36355F" borderRadius="full">
          <TagLabel fontWeight="bold">
            {isCompany ? 'Empresa' : 'Persona'}
          </TagLabel>
        </Tag>
      </Card>

      {/* Información de la empresa */}
      <Card>
        <SectionTitle>Información de la empresa</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
          <Field label="Nombre" value={client.company_name || client.name} />
          <Field label="Dirección" value={client.address} />
          <Field label="Región" value={getRegionName(client.region)} />
          <Field label="Comuna" value={getCommuneName(client.commune)} />
        </SimpleGrid>
      </Card>

      {/* Información de contacto */}
      <Card>
        <SectionTitle>Información de contacto</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
          <Field label="Encargado" value={client.name} />
          <Field label="Teléfono" value={client.phone ? `+56 ${client.phone}` : ''} />
        </SimpleGrid>
      </Card>

      {/* Sucursales (solo si tiene) */}
      {branchs.length > 0 && (
        <Card>
          <SectionTitle>Sucursal{branchs.length > 1 ? 'es' : ''}</SectionTitle>
          <Stack spacing={4}>
            {branchs.map((branch, index) => (
              <Box
                key={branch.id || index}
                border="1px solid"
                borderColor="#E2E8F0"
                borderRadius="lg"
                p={4}
              >
                <Text fontSize="14px" fontWeight="bold" color="#59BABA" mb={3}>
                  {branch.name || `Sucursal ${index + 1}`}
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Field label="Nombre" value={branch.name} />
                  <Field label="Dirección" value={branch.address} />
                  <Field label="Región" value={getRegionName(branch.region)} />
                  <Field label="Comuna" value={getCommuneName(branch.commune)} />
                </SimpleGrid>
              </Box>
            ))}
          </Stack>
        </Card>
      )}

      {/* Áreas a tratar */}
      <Card>
        <SectionTitle>Áreas a tratar</SectionTitle>
        {locations.length ? (
          <Wrap spacing={3}>
            {locations.map((loc) => (
              <WrapItem key={loc.id || loc.name}>
                <Tag size="lg" variant="subtle" colorScheme="teal" borderRadius="full">
                  <TagLabel>{loc.name}</TagLabel>
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        ) : (
          <Text fontSize="15px" color="#718096">
            Sin áreas registradas
          </Text>
        )}
      </Card>

      {/* Servicios contratados (sin valor) */}
      <Card>
        <SectionTitle>Servicios contratados</SectionTitle>
        {services.length ? (
          <Stack spacing={3}>
            {services.map((service, index) => {
              const types = service.service_types?.length
                ? service.service_types
                : service.service_type
                ? [service.service_type]
                : []
              return (
                <Box
                  key={index}
                  border="1px solid"
                  borderColor="#E2E8F0"
                  borderRadius="lg"
                  p={4}
                >
                  <Flex
                    justify="space-between"
                    align={{ base: 'start', md: 'center' }}
                    direction={{ base: 'column', md: 'row' }}
                    gap={3}
                  >
                    <Wrap spacing={2}>
                      {types.map((type) => (
                        <WrapItem key={type}>
                          <Tag bg="#D1F1F5" color="#36355F" borderRadius="md">
                            <TagLabel fontWeight="600">
                              {getServiceTypeLabel(type)}
                            </TagLabel>
                          </Tag>
                        </WrapItem>
                      ))}
                    </Wrap>
                    <HStack spacing={2} flexShrink={0}>
                      <Text fontSize="12px" color="#718096">
                        Frecuencia:
                      </Text>
                      <Text fontSize="14px" color="#36355F" fontWeight="600">
                        {getFrequencyLabel(service.frequency)}
                      </Text>
                    </HStack>
                  </Flex>
                  {types.includes('OTROS_SERVICIOS') &&
                    (() => {
                      const others = service.other_services?.length
                        ? service.other_services
                        : service.description
                        ? [service.description]
                        : []
                      if (!others.length) return null
                      return (
                        <>
                          <Divider my={3} />
                          <Text
                            fontSize="12px"
                            color="#718096"
                            textTransform="uppercase"
                            letterSpacing="0.5px"
                            mb={1}
                          >
                            Otros servicios
                          </Text>
                          <Wrap spacing={2}>
                            {others.map((other, i) => (
                              <WrapItem key={i}>
                                <Tag
                                  variant="subtle"
                                  colorScheme="teal"
                                  borderRadius="full"
                                >
                                  <TagLabel>{other}</TagLabel>
                                </Tag>
                              </WrapItem>
                            ))}
                          </Wrap>
                        </>
                      )
                    })()}
                </Box>
              )
            })}
          </Stack>
        ) : (
          <Text fontSize="15px" color="#718096">
            Sin servicios contratados
          </Text>
        )}
      </Card>

      {/* Estaciones de control de plagas */}
      <Card>
        <SectionTitle>Estaciones de control de plagas</SectionTitle>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <PestStat label="Estaciones rodenticidas" value={pest.rodenticide_stations} />
          <PestStat label="Trampas no tóxicas" value={pest.non_toxic_traps} />
          <PestStat label="Jaulas de palomas" value={pest.pigeon_cages} />
          <PestStat label="Equipos TUV" value={pest.tuv_equipment} />
        </SimpleGrid>
        {Array.isArray(pest.others) && pest.others.filter((o) => o?.name).length ? (
          <Box mt={4}>
            <Text fontSize="12px" color="#718096" textTransform="uppercase" letterSpacing="0.5px">
              Otros
            </Text>
            <Stack spacing={1} mt={1}>
              {pest.others
                .filter((o) => o?.name)
                .map((o, i) => (
                  <Text key={i} fontSize="15px" color="#36355F" fontWeight="500">
                    {o.name} ({o.quantity ?? 0})
                  </Text>
                ))}
            </Stack>
          </Box>
        ) : null}
      </Card>

      {/* Documento PDF */}
      {client.pdf && (
        <Card>
          <SectionTitle>Documento PDF</SectionTitle>
          <Flex
            align="center"
            gap={4}
            p={3}
            border="1px solid"
            borderColor="#E2E8F0"
            borderRadius="lg"
            transition="all 0.2s ease"
            _hover={{ borderColor: '#59BABA', boxShadow: 'sm' }}
          >
            <Flex align="center" justify="center" boxSize="44px" borderRadius="md" bg="#D1F1F5" flexShrink={0}>
              <Image src={addFileIcon} boxSize="22px" />
            </Flex>
            <Link
              href={client.pdf}
              isExternal
              flex="1"
              minW={0}
              color="#36355F"
              fontWeight="bold"
              fontSize="15px"
              noOfLines={1}
              wordBreak="break-all"
              _hover={{ color: '#59BABA', textDecoration: 'underline' }}
            >
              {client.pdf.substr(client.pdf.lastIndexOf('/') + 1)}
            </Link>
          </Flex>
        </Card>
      )}
    </Stack>
  )
}

export default ClientProfile
